"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { SyncDirection } from "../sync-direction-icon";
import { candidateSyncLabel } from "../../lib/sync-direction-copy";
import {
  isSyncActionEnabled,
  syncStateDescription,
} from "./sync-state-badge";

const KNOWN_STATES = [
  "not_synced",
  "queued",
  "synced",
  "error",
  "ineligible",
] as const;
type KnownState = (typeof KNOWN_STATES)[number];

/**
 * The per-state status dot. Syncing reuses the arbitrator `ai-ping-dot`
 * (amber, pulsing); every other state is a solid colored dot.
 */
function StatusDot({ state, paused }: { state: KnownState; paused?: boolean }) {
  if (state === "queued") {
    return (
      <span
        aria-hidden
        className="ai-ping-dot size-[9px] shrink-0 rounded-full bg-ai"
      />
    );
  }

  const color =
    state === "synced" && !paused
      ? "bg-[#22c55e] dark:bg-[#66dc7e]"
      : state === "error" || state === "ineligible"
        ? "bg-destructive dark:bg-[#ED6664]"
        : "bg-contrast/35"; // not_synced, paused synced

  return (
    <span
      aria-hidden
      className={cn("size-[9px] shrink-0 rounded-full", color)}
    />
  );
}

/**
 * Pipeline sync-column cell: a status dot + text label, mirroring the
 * arbitrator's `CalculatingLabel` (dot + `gap-2` + `text-sm` label). Syncing
 * shimmers like the arbitrator's "Evaluating"; other states use a muted /
 * state-tinted label. Clicking a syncable row (not synced / failed) opens sync.
 */
export function SyncColumnIndicator({
  state,
  error,
  paused,
  pendingAction,
  direction,
  isInSystemSourced,
  onSyncClick,
}: {
  state?: string;
  error?: string | null;
  paused?: boolean;
  direction?: SyncDirection | null;
  /** In-flight push action — drives Syncing vs Updating for `queued`. */
  pendingAction?: string | null;
  isInSystemSourced: boolean;
  onSyncClick?: () => void;
}) {
  if (!state || !KNOWN_STATES.includes(state as KnownState)) {
    return null;
  }

  const knownState = state as KnownState;
  const description = syncStateDescription(
    state,
    error,
    paused,
    pendingAction,
    direction,
  );
  const clickable =
    !!onSyncClick &&
    isSyncActionEnabled(state, isInSystemSourced, direction);

  const label =
    paused && state === "synced"
      ? "Paused"
      : candidateSyncLabel(state, direction, pendingAction);

  const labelClass =
    knownState === "queued"
      ? "ai-shimmer-text"
      : knownState === "synced" && !paused
        ? "text-[#15803d] dark:text-[#66dc7e]"
        : knownState === "error" || knownState === "ineligible"
          ? "text-destructive dark:text-[#ED6664]"
          : "text-muted-foreground";

  const content = (
    <div
      className={cn(
        "flex w-fit items-center gap-2 rounded-md",
        clickable && "-mx-1.5 px-1.5 py-1 cursor-pointer hover:bg-contrast/5",
      )}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onSyncClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSyncClick?.();
              }
            }
          : undefined
      }
      aria-label={clickable ? `${label} — click to sync` : label}
    >
      <StatusDot state={knownState} paused={paused} />
      <span
        className={cn(
          "truncate text-sm font-medium whitespace-nowrap",
          labelClass,
        )}
      >
        {label}
      </span>
    </div>
  );

  if (!description) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{description}</TooltipContent>
    </Tooltip>
  );
}
